<template>
    <button
        class="bg-transparent -m-2 p-2 hover:bg-gray-100 rounded-md relative"
        @click="toggleContent"
    >
        <ion-icon ref="iconRef" name="notifications-outline"></ion-icon>
        <span
            v-if="hasNewNotifications"
            class="block absolute top-[-5px] right-0.5 h-auto"
        >
            <span class="bg-red-500 text-white text-[10px] rounded-full px-1">
                {{
                    state.notifications.filter(
                        (notification) => !notification.read_at
                    ).length
                }}
            </span>
        </span>
    </button>
    <Teleport to="body" v-show="isContentVisible">
        <div
            v-show="isContentVisible"
            class="fixed top-0 left-0 w-full h-full z-10 bg-white bg-opacity-10"
            @click.self="toggleContent"
        ></div>
        <div
            ref="contentRef"
            v-show="isContentVisible"
            id="app-notification-box"
            class="
                bg-white
                shadow-lg
                rounded-md
                border border-gray-200
                h-[400px]
                w-[300px]
                overflow-y-scroll
                z-20
            "
        >
            <div
                v-if="state.error"
                class="
                    flex
                    justify-between
                    items-center
                    p-4
                    border-b border-gray-200
                "
            >
                <div class="text-red-500">{{ state.error }}</div>
                <button
                    class="text-gray-500 hover:text-gray-700"
                    @click="fetchNotifications"
                >
                    <ion-icon name="refresh-outline"></ion-icon>
                </button>
            </div>
            <div
                v-if="state.loading"
                class="flex justify-center items-center h-full"
            >
                <simple-loading-circle></simple-loading-circle>
            </div>
            <div
                v-if="!state.notifications || state.notifications.length === 0"
                class="flex justify-center items-center h-full"
            >
                <div class="text-gray-500">No notifications</div>
            </div>
            <ul>
                <li
                    v-for="notification in state.notifications"
                    class="
                        p-4
                        border-b border-gray-200
                        text-sm
                        hover:bg-gray-100
                    "
                    :class="{
                        'opacity-30 hover:opacity-80': notification.read_at,
                        'bg-primary bg-opacity-10': !notification.read_at,
                    }"
                >
                    <a
                        class="block"
                        :href="notification.action ? notification.action : '#'"
                        @click.prevent="markRead(notification, true)"
                    >
                        <div
                            class="
                                uppercase
                                font-semibold
                                text-xs text-gray-500
                                mb-1
                            "
                        >
                            {{ notification.metadata?.type }}
                        </div>
                        <div class="normal-case">
                            {{ notification.message }}
                        </div>
                    </a>
                    <div
                        class="
                            text-xs
                            mt-1
                            text-gray-400
                            flex
                            items-center
                            gap-1
                        "
                    >
                        <div
                            class="flex-grow flex-shrink capitalize"
                            v-if="notification.created_at"
                        >
                            {{ relativeDate(notification.created_at) }}
                        </div>
                        <button
                            v-if="!notification.read_at"
                            class="
                                ml-auto
                                text-gray-500
                                hover:text-primary
                                outline-none
                                p-1
                            "
                            @click="markRead(notification)"
                        >
                            Mark as read
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </Teleport>
</template>

<script setup>
import { createPopper } from "@popperjs/core";
import { computed, onMounted, reactive, ref } from "vue";
import SimpleLoadingCircle from "./SimpleLoadingCircle.vue";

const props = defineProps({
    notificationApiUrl: {
        type: String,
        required: true,
    },
    userId: {
        type: Number,
        required: true,
    },
});
const isContentVisible = ref(false);
const iconRef = ref(null);
const contentRef = ref(null);

const toggleContent = () => {
    isContentVisible.value = !isContentVisible.value;
    if (isContentVisible.value) {
        createPopper(iconRef.value, contentRef.value, {
            placement: "bottom-end",
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset: [0, 30],
                    },
                },
            ],
        }).forceUpdate();
    }
};

const state = reactive({
    notifications: [],
    loading: false,
    error: null,
    nextUrl: null,
});

function relativeDate(date) {
    const olderDate = new Date(date);
    const currentDate = new Date();

    const diff = olderDate - currentDate;

    const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    return formatter.format(Math.round(diff / 86400000), "day");
}

async function markRead(notification, navigate = false) {
    notification.read_at = new Date().toISOString();
    try {
        await axios.post(
            `${props.notificationApiUrl}/${notification.id}/mark-read`
        );
    } catch (e) {
        console.log(e);
    }
    if (navigate && notification.action !== "") {
        window.location = notification.action;
    }
}

async function fetchNotifications() {
    state.loading = true;
    state.error = null;
    try {
        const res = await axios.get(props.notificationApiUrl);
        state.notifications = res.data.data;
        state.nextUrl = res.data.next_page_url;
    } catch (e) {
        state.error = "Error fetching notifications, please try again later.";
        console.log(e);
    }
    state.loading = false;
}

const hasNewNotifications = computed(() => {
    return state.notifications.some((notification) => !notification.read_at);
});

onMounted(() => {
    fetchNotifications();

    Echo.private(`Notification.${props.userId}`).listen(
        "NewNotificationEvent",
        (e) => {
            console.log(e);
            state.notifications.unshift(e.notification);
        }
    );

    Echo.channel("channel-notifications-" + props.userId).listen(
        "event-notifications-" + props.userId,
        (e) => {
            fetchNotifications();
        }
    );

    Echo.channel("channel-notifications-forum").listen(
        "event-notifications-forum",
        (e) => {
            fetchNotifications();
        }
    );
});
</script>

<style scoped></style>
